import moment from "moment";

export interface FilePathGeneratorOptions {
  /** Base path prefix for the file (e.g., "nycbs_sftp_csv") */
  basePath?: string;
  /** Suffix to insert before the date pattern in filename (e.g., "_response") */
  filenameSuffix?: string;
  /** Regex pattern to match the date suffix in filename (default: matches _YYYYMMDDHHMM.csv) */
  datePattern?: RegExp;
  /** Date format for the timestamp prefix (default: "YYYYMMDD_HHmmss") */
  timestampFormat?: string;
}

const DEFAULT_OPTIONS: Required<FilePathGeneratorOptions> = {
  basePath: "nycbs_sftp_csv",
  filenameSuffix: "_response",
  datePattern: /(_\d{12}\.csv)$/,
  timestampFormat: "YYYYMMDD_HHmmss",
};

/**
 * Transforms the filename by inserting a suffix before the date pattern
 * Example: NYCBS_RXSM_RisaHealth_202601151305.csv → NYCBS_RXSM_RisaHealth_response_202601151305.csv
 */
export const transformFilename = (
  filename: string,
  suffix: string,
  datePattern: RegExp,
): string => {
  return filename.replace(datePattern, `${suffix}$1`);
};

/**
 * Generates a dynamic file path for GCS/SFTP downloads
 *
 * Path format: {basePath}/{orgId}/{year}/{month:02d}/{timestamp}_{transformedFilename}
 *
 * @param orgId - Organization ID
 * @param filename - Original filename from API
 * @param dumpedAt - Timestamp when the file was dumped (ISO string or Date)
 * @param options - Optional configuration for path generation
 * @returns Generated file path string
 *
 * @example
 * // Default usage
 * generateDownloadFilePath("org123", "NYCBS_RXSM_RisaHealth_202601151305.csv", "2026-01-27T12:52:36Z")
 * // Returns: "nycbs_sftp_csv/org123/2026/01/20260127_125236_NYCBS_RXSM_RisaHealth_response_202601151305.csv"
 *
 * @example
 * // Custom configuration
 * generateDownloadFilePath("org123", "report_202601151305.csv", "2026-01-27T12:52:36Z", {
 *   basePath: "custom_path",
 *   filenameSuffix: "_processed",
 * })
 */
export const generateDownloadFilePath = (
  orgId: string,
  filename: string,
  dumpedAt: string | Date,
  options: FilePathGeneratorOptions = {},
): string => {
  const config = { ...DEFAULT_OPTIONS, ...options };

  const timestamp = moment(dumpedAt);
  const year = timestamp.year();
  const month = String(timestamp.month() + 1).padStart(2, "0"); // moment months are 0-indexed
  const timestampPrefix = timestamp.format(config.timestampFormat);

  const transformedFilename = transformFilename(
    filename,
    config.filenameSuffix,
    config.datePattern,
  );

  return `${config.basePath}/${orgId}/${year}/${month}/${timestampPrefix}_${transformedFilename}`;
};

/**
 * Generates only the directory path (without filename) for a given org and timestamp
 */
export const generateDirectoryPath = (
  orgId: string,
  dumpedAt: string | Date,
  basePath: string = DEFAULT_OPTIONS.basePath,
): string => {
  const timestamp = moment(dumpedAt);
  const year = timestamp.year();
  const month = String(timestamp.month() + 1).padStart(2, "0");

  return `${basePath}/${orgId}/${year}/${month}`;
};

/**
 * Generates the year-level path for listing month prefixes.
 * Path format: {basePath}/{orgId}/{year} e.g. "nycbs_sftp_csv/orgId/2026"
 */
export const generateYearPath = (
  orgId: string,
  year: number,
  basePath: string = DEFAULT_OPTIONS.basePath,
): string => {
  return `${basePath}/${orgId}/${year}`;
};
